import { useCallback, useEffect, useState } from "react";
import { IceAktarYolu } from "../kabiliyet/IceAktarYolu";
import type { Account, AccountKind, HesapRengi, NewAccount, OksuzHareketGrubu } from "@parafon/contracts/local-api";
import { HESAP_RENKLERI } from "@parafon/contracts/local-api";
import { isKnownCurrency } from "@parafon/contracts";
import { formatIsoDate } from "../lib/format";
import { newId } from "../lib/id";
import { routeSorgusunuAl, useRoute } from "../lib/router";
import { useYedek } from "../yedek/YedekSaglayici";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { useAsync, useMutation } from "../repo/useAsync";
import { Confirm, Modal } from "../ui/Modal";
import { Aciklama } from "../ui/Aciklama";
import { CurrencySelect } from "../ui/CurrencySelect";
import { HareketSecimi, VARSAYILAN_SECIM, secimDegeri, type HareketSecimiDurumu } from "../components/HareketSecimi";
import { HESAP_RENK_ADI, HesapAdi, hesapRengiCss, hesapSeridi } from "../ui/HesapAdi";
import { KilitliKabiliyet } from "../kabiliyet/KilitliKabiliyet";
import { useHesapErisimi, type HesapErisimi } from "../kabiliyet/useHesapErisimi";
import {
  Button,
  DateInput,
  EmptyState,
  ErrorBlock,
  Field,
  InlineError,
  LoadingBlock,
  Notice,
  Screen,
  Tag,
  TextArea,
  TextInput,
  cx,
} from "../ui/primitives";
import { M } from "../dil";

export function AccountsScreen() {
  const { repo, revision, invalidate } = useRepositoryContext();
  const [editing, setEditing] = useState<Account | "new" | null>(null);
  // Kasa açılınca ilk sayfa buysa giriş formu açık gelir (`lib/acilisSayfasi.ts`).
  useEffect(() => {
    if (routeSorgusunuAl("yeni") === "1") setEditing("new");
  }, []);
  const [deleting, setDeleting] = useState<Account | null>(null);
  const remove = useMutation();
  const [, navigate] = useRoute();
  // Yedek sağlayıcısı yoksa (bazı test/sahte kurulumlar) yönlendirme gösterilmez.
  const yedekVar = useYedek() !== null;
  // Gelir modeli: hesap sınırı ve (süre bitince) düzenlenebilir hesaplar.
  const erisim = useHesapErisimi();

  // Devre dışı hesaplar YALNIZCA burada görünür (kullanıcı kararı 25.10.2026).
  const load = useCallback(() => repo.accounts.list(false, true), [repo]);
  const devreRun = useMutation();
  const devreDegistir = (a: Account): void => {
    void devreRun.run(async () => {
      await repo.accounts.update(a.id, { devreDisi: a.devreDisi !== true });
      invalidate();
    });
  };
  const { state, reload } = useAsync(load, [repo, revision]);

  // Silinecek hesabın canlı hareket sayısı (pencere açılınca okunur).
  const [secim, setSecim] = useState<HareketSecimiDurumu>(VARSAYILAN_SECIM);
  const [silinecekHareket, setSilinecekHareket] = useState<number | null>(null);
  const silmeyiAc = (a: Account): void => {
    setDeleting(a);
    setSecim(VARSAYILAN_SECIM);
    setSilinecekHareket(null);
    void repo.transactions.query({ accountIds: [a.id], limit: 1 }).then(
      (sayfa) => setSilinecekHareket(sayfa.total),
      () => setSilinecekHareket(null),
    );
  };

  const onDelete = async (): Promise<void> => {
    if (deleting === null) return;
    const deger = secimDegeri(secim);
    if (deger === null) return;
    const ok = await remove.run(() => repo.accounts.remove(deleting.id, deger));
    if (ok) {
      setDeleting(null);
      invalidate();
    }
  };

  // ÖKSÜZ HAREKETLER: silinmiş hesaplara bağlı kalmış hareketler (bu karar
  // öncesinde silinen hesaplardan). Toplamlara giriyorlar; kullanıcı çözer.
  const oksuzYukle = useCallback(async () => (await repo.accounts.oksuzHareketler?.()) ?? [], [repo]);
  const oksuz = useAsync(oksuzYukle, [repo, revision]);
  const [cozulen, setCozulen] = useState<OksuzHareketGrubu | null>(null);
  const [cozSecim, setCozSecim] = useState<HareketSecimiDurumu>(VARSAYILAN_SECIM);
  const coz = useMutation();
  const onCoz = async (): Promise<void> => {
    if (cozulen === null || repo.accounts.oksuzHareketleriCoz === undefined) return;
    const deger = secimDegeri(cozSecim);
    if (deger === null) return;
    const f = repo.accounts.oksuzHareketleriCoz;
    const ok = await coz.run(async () => {
      await f(cozulen.accountId, deger);
    });
    if (ok) {
      setCozulen(null);
      invalidate();
    }
  };

  return (
    <Screen
      title={M.hesaplar.ekran.baslik}
      description={M.hesaplar.ekran.aciklama}
      toolbar={
        <>
          <Button variant="primary" disabled={!erisim.yeniHesap} onClick={() => setEditing("new")}>
            {M.hesaplar.ekran.hesapEkle}
          </Button>
          <IceAktarYolu />
        </>
      }
    >
      {!erisim.yeniHesap && (
        <div className="px-4 pt-3">
          <KilitliKabiliyet
            ne={M.hesaplar.ekran.yeniHesap}
            aciklama={M.hesaplar.ekran.ucretsizSinir(erisim.hesapSiniri ?? 0)}
          />
        </div>
      )}
      {erisim.duzenlenebilir !== null && <DuzenlenebilirSecimi erisim={erisim} />}
      {(oksuz.state.data ?? []).length > 0 && (
        <div className="px-4 pt-3">
          <Notice tone="warn" title={M.hesaplar.ekran.oksuzBaslik}>
            <p>{M.hesaplar.ekran.oksuzMetin}</p>
            <ul className="mt-2 space-y-1.5">
              {(oksuz.state.data ?? []).map((g) => (
                <li key={g.accountId} className="flex flex-wrap items-center gap-2">
                  <HesapAdi renk={g.renk} className="font-medium">
                    {g.ad}
                  </HesapAdi>
                  <span className="text-muted">{M.hesaplar.ekran.oksuzSatir(g.hareketSayisi)}</span>
                  <Button
                    onClick={() => {
                      setCozulen(g);
                      setCozSecim(VARSAYILAN_SECIM);
                    }}
                  >
                    {M.hesaplar.ekran.coz}
                  </Button>
                </li>
              ))}
            </ul>
          </Notice>
        </div>
      )}
      {state.status === "loading" && state.data === undefined && <LoadingBlock label={M.hesaplar.ekran.yukleniyor} />}
      {state.status === "error" && <ErrorBlock error={state.error} onRetry={reload} />}

      {state.data !== undefined &&
        (state.data.length === 0 ? (
          <EmptyState
            title={M.hesaplar.ekran.bosBaslik}
            description={
              yedekVar
                ? M.hesaplar.ekran.bosYedekli
                : M.hesaplar.ekran.bos
            }
            action={
              <div className="flex flex-wrap justify-center gap-2">
                <Button variant="primary" onClick={() => setEditing("new")}>
                  {M.hesaplar.ekran.ilkHesap}
                </Button>
                {/* Yeni cihazdaki kullanıcı Ayarlar'a değil buraya düşüyor;
                    yedeğin nerede olduğunu bilmeyebilir (kullanıcı kararı 06.10). */}
                {yedekVar && <Button onClick={() => navigate("ayarlar")}>{M.hesaplar.ekran.buluttanGeriYukle}</Button>}
              </div>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="pf-table" data-kart-bolum="liste">
              <caption className="sr-only">{M.hesaplar.ekran.tabloBaslik}</caption>
              <thead>
                <tr>
                  <th scope="col">{M.hesaplar.ekran.sutun.ad}</th>
                  <th scope="col">{M.hesaplar.ekran.sutun.kurum}</th>
                  <th scope="col">{M.hesaplar.ekran.sutun.paraBirimi}</th>
                  <th scope="col">{M.hesaplar.ekran.sutun.acilis}</th>
                  <th scope="col">{M.hesaplar.ekran.sutun.kapanis}</th>
                  <th scope="col" className="w-40 text-right">
                    {M.hesaplar.ekran.sutun.islem}
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.data.map((a) => (
                  <tr key={a.id} className={a.devreDisi === true ? "opacity-60" : undefined}>
                    <td style={hesapSeridi(a.renk)}>
                      <HesapAdi renk={a.renk} className="font-medium">
                        {a.name}
                      </HesapAdi>
                      {a.devreDisi === true && (
                        <span className="ml-2">
                          <Tag>{M.hesaplar.ekran.devreDisi}</Tag>
                        </span>
                      )}
                      {a.devreDisi !== true && !erisim.duzenlenebilirMi(a.id) && (
                        <span className="ml-2">
                          <Tag>{M.hesaplar.ekran.saltOkunur}</Tag>
                        </span>
                      )}
                      {a.note !== undefined && a.note !== "" && (
                        <span className="ml-2 text-[11px] text-faint">{a.note}</span>
                      )}
                    </td>
                    <td className="text-muted">{a.institution ?? "—"}</td>
                    {/* TANINMAYAN KOD BURADA GÖRÜNÜR OLMALI: yeni hareket
                        formu artık onu devralmıyor ama hesabın kendisi bozuk
                        kalıyor ve kullanıcı nereyi düzelteceğini bilmeli. */}
                    <td className="pf-fig">
                      {isKnownCurrency(a.currency) ? (
                        a.currency
                      ) : (
                        <Aciklama
                          className="pf-aciklama-isaretli text-warn"
                          icerik={
                            <span className="pf-aciklama-satir">{M.hesaplar.ekran.taninmayanKod(a.currency)}</span>
                          }
                        >
                          {a.currency}
                        </Aciklama>
                      )}
                    </td>
                    <td className="pf-fig text-muted">{formatIsoDate(a.openedAt)}</td>
                    <td className="pf-fig text-muted">{formatIsoDate(a.closedAt)}</td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          className="!h-6 !px-2 text-[12px]"
                          disabled={a.devreDisi !== true && !erisim.duzenlenebilirMi(a.id)}
                          onClick={() => setEditing(a)}
                        >
                          {M.ortak.duzenle}
                        </Button>
                        <Button
                          className="!h-6 !px-2 text-[12px]"
                          disabled={devreRun.pending || (a.devreDisi === true && !erisim.yeniHesap)}
                          title={
                            a.devreDisi === true
                              ? M.hesaplar.ekran.tekrarAcIpucu
                              : M.hesaplar.ekran.devreDisiIpucu
                          }
                          onClick={() => devreDegistir(a)}
                        >
                          {a.devreDisi === true ? M.hesaplar.ekran.tekrarAc : M.hesaplar.ekran.devreDisiBirak}
                        </Button>
                        <Button
                          variant="danger"
                          className="!h-6 !px-2 text-[12px]"
                          onClick={() => silmeyiAc(a)}
                        >
                          {M.ortak.sil}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

      {devreRun.error !== undefined && (
        <div className="px-4">
          <InlineError error={devreRun.error} />
        </div>
      )}

      <Modal
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        title={editing === "new" ? M.hesaplar.ekran.hesapEkle : M.hesaplar.ekran.hesabiDuzenle}
      >
        {editing !== null && (
          <AccountForm
            account={editing === "new" ? null : editing}
            yeniHesap={erisim.yeniHesap}
            onDone={() => {
              setEditing(null);
              invalidate();
            }}
            onCancel={() => setEditing(null)}
          />
        )}
      </Modal>

      <Confirm
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={M.hesaplar.ekran.silBaslik}
        body={
          <>
            <p>
              <strong>
                <HesapAdi renk={deleting?.renk}>{deleting?.name}</HesapAdi>
              </strong>
              {M.hesaplar.ekran.silSonEk}
            </p>
            {silinecekHareket !== null && silinecekHareket > 0 && (
              <HareketSecimi
                idOnek="sil"
                hareketSayisi={silinecekHareket}
                hedefler={(state.data ?? []).filter((a) => a.id !== deleting?.id)}
                deger={secim}
                onChange={setSecim}
              />
            )}
            {silinecekHareket === 0 && <p className="mt-2">{M.hesaplar.ekran.hareketYok}</p>}
            <InlineError error={remove.error} />
          </>
        }
        confirmLabel={secim.tur === "tasi" && (silinecekHareket ?? 0) > 0 ? M.hesaplar.ekran.tasiVeSil : M.ortak.sil}
        onConfirm={() => void onDelete()}
        pending={remove.pending}
        disabled={silinecekHareket === null || ((silinecekHareket ?? 0) > 0 && secimDegeri(secim) === null)}
      />

      <Confirm
        open={cozulen !== null}
        onOpenChange={(open) => !open && setCozulen(null)}
        title={M.hesaplar.ekran.cozBaslik}
        body={
          <>
            <p>
              <strong>
                <HesapAdi renk={cozulen?.renk}>{cozulen?.ad}</HesapAdi>
              </strong>{" "}
              {M.hesaplar.ekran.cozSonEk}
            </p>
            <HareketSecimi
              idOnek="coz"
              hareketSayisi={cozulen?.hareketSayisi ?? 0}
              hedefler={state.data ?? []}
              deger={cozSecim}
              onChange={setCozSecim}
            />
            <InlineError error={coz.error} />
          </>
        }
        confirmLabel={cozSecim.tur === "tasi" ? M.hesaplar.ekran.tasi : M.hesaplar.ekran.hareketleriSil}
        onConfirm={() => void onCoz()}
        pending={coz.pending}
        disabled={secimDegeri(cozSecim) === null}
      />
    </Screen>
  );
}

// ---------------------------------------------------------------------------

interface AccountFormProps {
  readonly account: Account | null;
  /** Hesap sınırı izin veriyor mu (yalnızca yeni hesapta bakılır). */
  readonly yeniHesap: boolean;
  readonly onDone: () => void;
  readonly onCancel: () => void;
}

function AccountForm({ account, yeniHesap, onDone, onCancel }: AccountFormProps) {
  const { repo } = useRepositoryContext();
  const save = useMutation();

  const [name, setName] = useState(account?.name ?? "");
  // Hesap türü ekranda yok (kullanıcı kararı 01.11.2026: hiçbir etkisi yoktu).
  // Var olan hesabın kasadaki değeri korunur; yeni hesaba sabit varsayılan.
  const kind: AccountKind = account?.kind ?? "araci_kurum";
  const [institution, setInstitution] = useState(account?.institution ?? "");
  const [currency, setCurrency] = useState(account?.currency ?? "TRY");
  const [openedAt, setOpenedAt] = useState(account?.openedAt ?? "");
  const [closedAt, setClosedAt] = useState(account?.closedAt ?? "");
  const [note, setNote] = useState(account?.note ?? "");
  const [renk, setRenk] = useState<HesapRengi | null>(account?.renk ?? null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const submit = async (): Promise<void> => {
    const errors: Record<string, string> = {};
    if (name.trim() === "") errors["name"] = M.hesaplar.form.adZorunlu;
    if (!isKnownCurrency(currency)) errors["currency"] = M.hesaplar.form.paraBirimiSecin;
    if (closedAt !== "" && openedAt !== "" && closedAt < openedAt) {
      errors["closedAt"] = M.hesaplar.form.kapanisOnce;
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const base: NewAccount = {
      id: account?.id ?? newId(),
      name: name.trim(),
      kind,
      currency: currency.trim().toUpperCase(),
      ...(institution.trim() === "" ? {} : { institution: institution.trim() }),
      ...(openedAt === "" ? {} : { openedAt }),
      ...(closedAt === "" ? {} : { closedAt }),
      ...(note.trim() === "" ? {} : { note: note.trim() }),
    };

    const ok = await save.run(async () => {
      if (account === null && !yeniHesap) throw new Error(M.hesaplar.form.sinirDoldu);
      if (account === null) await repo.accounts.create(renk === null ? base : { ...base, renk });
      // Güncellemede boşaltılan alan SİLİNİR (`null`): eskiden boş kurum/not
      // kayda yansımıyordu.
      else
        await repo.accounts.update(account.id, {
          ...base,
          institution: base.institution ?? null,
          openedAt: base.openedAt ?? null,
          closedAt: base.closedAt ?? null,
          note: base.note ?? null,
          renk,
        });
    });
    if (ok) onDone();
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="space-y-3"
      noValidate
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={M.hesaplar.form.hesapAdi} htmlFor="acc-name" required {...pick(fieldErrors, "name")}>
          <TextInput
            id="acc-name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            aria-invalid={fieldErrors["name"] !== undefined}
          />
        </Field>

        <Field label={M.hesaplar.form.kurum} htmlFor="acc-inst" hint={M.hesaplar.form.kurumIpucu}>
          <TextInput id="acc-inst" value={institution} onChange={(e) => setInstitution(e.currentTarget.value)} />
        </Field>

        <Field
          label={M.hesaplar.form.anaParaBirimi}
          htmlFor="acc-cur"
          required
          hint={M.hesaplar.form.paraBirimiIpucu}
          {...pick(fieldErrors, "currency")}
        >
          <CurrencySelect
            id="acc-cur"
            className="pf-fig"
            value={currency}
            onChange={setCurrency}
            invalid={fieldErrors["currency"] !== undefined}
          />
        </Field>

        <Field label={M.hesaplar.form.acilisTarihi} htmlFor="acc-open">
          <DateInput id="acc-open" value={openedAt} onChange={(e) => setOpenedAt(e.currentTarget.value)} />
        </Field>

        <Field label={M.hesaplar.form.kapanisTarihi} htmlFor="acc-close" {...pick(fieldErrors, "closedAt")}>
          <DateInput id="acc-close" value={closedAt} onChange={(e) => setClosedAt(e.currentTarget.value)} />
        </Field>
      </div>

      <fieldset>
        <legend className="mb-1 text-[12px] font-semibold text-muted">{M.hesaplar.form.renk}</legend>
        <p className="mb-2 text-[12px] text-faint">
          {M.hesaplar.form.renkAciklama}
        </p>
        <div role="radiogroup" aria-label={M.hesaplar.form.hesapRengi} className="flex flex-wrap gap-1.5">
          {([null, ...HESAP_RENKLERI] as const).map((r) => {
            const secili = renk === r;
            return (
              <button
                key={r ?? "yok"}
                type="button"
                role="radio"
                aria-checked={secili}
                onClick={() => setRenk(r)}
                className={cx(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px]",
                  secili ? "border-ink ring-1 ring-ink" : "border-line",
                )}
              >
                <span
                  aria-hidden="true"
                  className={cx("inline-block size-3 rounded-full", r === null && "border border-line-strong")}
                  style={r === null ? undefined : { background: hesapRengiCss(r) }}
                />
                {r === null ? M.hesaplar.form.renksiz : <HesapAdi renk={r}>{HESAP_RENK_ADI[r]}</HesapAdi>}
              </button>
            );
          })}
        </div>
      </fieldset>

      <Field label={M.hesaplar.form.not} htmlFor="acc-note">
        <TextArea id="acc-note" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
      </Field>

      <InlineError error={save.error} />

      <div className="pf-pencere-alt flex justify-end gap-2 border-t border-line">
        <Button onClick={onCancel}>{M.ortak.vazgec}</Button>
        <Button type="submit" variant="primary" disabled={save.pending}>
          {save.pending ? M.ortak.kaydediliyor : M.ortak.kaydet}
        </Button>
      </div>
    </form>
  );
}

/** `exactOptionalPropertyTypes` altında koşullu prop geçirme yardımcısı. */
function pick(errors: Record<string, string>, key: string): { error?: string } {
  const v = errors[key];
  return v === undefined ? {} : { error: v };
}

/**
 * Süre bitince (ya da hesap sınırı düşünce) etkin hesaplar sınırı aşıyorsa:
 * hangi hesapların düzenlenebilir kalacağını kullanıcı seçer, diğerleri salt
 * okunur kalır — silinmez, raporlarda görünür (kullanıcı kararı 26.10.2026).
 */
function DuzenlenebilirSecimi({ erisim }: { readonly erisim: HesapErisimi }) {
  const sinir = erisim.hesapSiniri ?? 0;
  const [secili, setSecili] = useState<string[]>(() => [...(erisim.duzenlenebilir ?? [])]);
  useEffect(() => setSecili([...(erisim.duzenlenebilir ?? [])]), [erisim.duzenlenebilir]);
  const kaydet = useMutation();
  const degistir = (id: string, acik: boolean): void =>
    setSecili((s) => (acik ? (s.length >= sinir ? s : [...s, id]) : s.filter((x) => x !== id)));
  return (
    <section aria-label={M.hesaplar.duzenlenebilir.bolum} className="px-4 pt-3">
      <KilitliKabiliyet
        ne={M.hesaplar.duzenlenebilir.ne(sinir)}
        aciklama={M.hesaplar.duzenlenebilir.aciklama(sinir)}
      />
      <ul className="mt-2 space-y-1 text-[13px]">
        {erisim.etkinHesaplar.map((a) => (
          <li key={a.id}>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={secili.includes(a.id)}
                disabled={!secili.includes(a.id) && secili.length >= sinir}
                onChange={(e) => degistir(a.id, e.currentTarget.checked)}
              />
              {a.name}
            </label>
          </li>
        ))}
      </ul>
      <div className="mt-2">
        <Button
          disabled={kaydet.pending || secili.length === 0}
          onClick={() => void kaydet.run(() => erisim.secimKaydet(secili))}
        >
          {M.hesaplar.duzenlenebilir.secimiKaydet}
        </Button>
      </div>
      <InlineError error={kaydet.error} />
    </section>
  );
}

